class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      // Sidebar filters selectors
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;

    this.currentPage = 1;
    this.paginationType; // "paginate" | "loadmore" | "infinite"
    this.totalPages;
    this.totalProducts;
    this.infiniteLoadingObserver;

    this.showColSwitchers;
    this.cachedCol;
    this.initialCol;
    this.activeCol;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
    };

    this.enableFilters;
    this.enableSorting;
    this.filtersType; // "storefront_filters" | "tags_filter"
    this.filtersPosition; // "leftColumn" | "rightColumn" | "fixed"
    this.accordions;
    this.sideEffectEventsAdded = false;
    this.sidebarOpen = false;
    this.sidebarTrigger = null;
    this.sidebarCloseTimeout = null;
    this.sidebarResizeHandler = null;
    this.sidebarScrollLocked = false;
  }

  init = () => {
    this.resetSidebarState();
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.sideEffectEventsAdded = true;
    MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());

    // Handle sticky toolbar on mobile
    this.handleStickyToolbar();
    document.addEventListener("matchMobile", () => this.handleStickyToolbar());
    document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    // Grid view data
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.view = section.dataset.view;
    // TODO: get from URL Search Params
    this.activeCol = null;
    this.currentPage = 1;
    this.initialCol = Number(section.dataset.initialColumn);
    this.cachedCol = Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    // Filters data
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
  };

  initGridView = () => {
    this.toggleView(this.isDesignMode ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();
    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.toggleView(Number(colNode.dataset.column)),
    });
    this.initLoadMore();
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar, openSidebar, closeSidebar } = this.domNodes;
    if (!sidebar || !openSidebar || !closeSidebar) return;
    this.syncSidebarAccessibility();
    window.requestAnimationFrame(this.initAccordions);
    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    openSidebar.addEventListener("keydown", this.handleSidebarControlKeydown);
    closeSidebar.addEventListener("keydown", this.handleSidebarControlKeydown);
    sidebar.addEventListener("click", (e) => e.target === sidebar && this.closeSidebarFilter());
    if (!this.sideEffectEventsAdded) {
      this.sidebarResizeHandler = debounce(this.syncSidebarAccessibility, 200);
      window.addEventListener("resize", this.sidebarResizeHandler);
    }
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;
    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.closeMobileSorting);
    sortingWrapper.addEventListener("click", (e) => e.target === sortingWrapper && this.closeMobileSorting());
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = (e) => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => sortingWrapper.style.setProperty("display", "none"), 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty('opacity', '1')
    });
  };

  isSidebarModal = () => {
    const sidebar = this.domNodes?.sidebar;
    return Boolean(sidebar && (window.innerWidth < 1280 || sidebar.dataset.type === "fixed"));
  };

  syncSidebarAccessibility = () => {
    const { sidebar, sidebarContent, openSidebar, closeSidebar, section } = this.domNodes || {};
    if (!this.enableFilters || !sidebar || !sidebarContent || !openSidebar) return;
    const controlledSidebarId = openSidebar.getAttribute("aria-controls");
    if (controlledSidebarId && !sidebar.id) sidebar.id = controlledSidebarId;

    if (this.isSidebarModal()) {
      sidebarContent.setAttribute("role", "dialog");
      sidebarContent.setAttribute("aria-modal", "true");
      sidebarContent.setAttribute("tabindex", "-1");
      openSidebar.setAttribute("aria-expanded", this.sidebarOpen ? "true" : "false");

      if (!this.sidebarOpen && sidebarContent.contains(document.activeElement)) {
        this.focusSidebarElement(openSidebar);
      }
      sidebar.setAttribute("aria-hidden", this.sidebarOpen ? "false" : "true");
      return;
    }

    const focusWasOnClose = closeSidebar && document.activeElement === closeSidebar;
    this.sidebarOpen = false;
    this.stopSidebarFocusManagement();
    this.unlockSidebarScroll();
    section?.classList.remove("sidebar-open");
    openSidebar.setAttribute("aria-expanded", "false");
    sidebar.setAttribute("aria-hidden", "false");
    sidebar.style.removeProperty("display");
    sidebar.style.removeProperty("--m-bg-opacity");
    sidebarContent.style.removeProperty("--m-translate-x");
    sidebarContent.removeAttribute("role");
    sidebarContent.removeAttribute("aria-modal");

    if (focusWasOnClose) {
      const nextFocusable = this.getSidebarFocusableElements().find((element) => element !== closeSidebar);
      this.focusSidebarElement(nextFocusable || sidebarContent);
    }
    sidebarContent.removeAttribute("tabindex");
  };

  openSidebarFilter = (event) => {
    if (!this.isSidebarModal()) return;
    const { sidebar, sidebarContent, openSidebar, section } = this.domNodes;
    if (this.sidebarCloseTimeout) {
      clearTimeout(this.sidebarCloseTimeout);
      this.sidebarCloseTimeout = null;
    }

    this.sidebarOpen = true;
    this.sidebarTrigger = event?.currentTarget || openSidebar;
    sidebar.setAttribute("aria-hidden", "false");
    openSidebar.setAttribute("aria-expanded", "true");
    sidebar.style.display = "block";
    section.classList.add("sidebar-open");
    this.lockSidebarScroll();
    this.startSidebarFocusManagement();
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
      this.focusSidebarInitial();
    });
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, openSidebar, section } = this.domNodes;
    if (!this.isSidebarModal()) {
      this.syncSidebarAccessibility();
      return;
    }

    this.sidebarOpen = false;
    this.stopSidebarFocusManagement();
    openSidebar.setAttribute("aria-expanded", "false");
    this.restoreSidebarFocus();
    sidebar.setAttribute("aria-hidden", "true");
    section.classList.remove("sidebar-open");
    sidebarContent.style.setProperty("--m-translate-x", "-100%");
    sidebar.style.removeProperty("--m-bg-opacity");
    this.unlockSidebarScroll();

    if (this.sidebarCloseTimeout) clearTimeout(this.sidebarCloseTimeout);
    const closingSidebar = sidebar;
    this.sidebarCloseTimeout = setTimeout(() => {
      if (!this.sidebarOpen && this.domNodes.sidebar === closingSidebar) {
        closingSidebar.style.removeProperty("display");
      }
      this.sidebarCloseTimeout = null;
    }, 300);
  };

  getSidebarFocusableElements = () => {
    const sidebarContent = this.domNodes?.sidebarContent;
    if (!sidebarContent) return [];
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type='hidden'])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
    ].join(",");
    return Array.from(sidebarContent.querySelectorAll(selector)).filter(
      (element) =>
        element.getClientRects().length > 0 && element.getAttribute("aria-hidden") !== "true"
    );
  };

  focusSidebarElement = (element) => {
    if (!element?.focus) return;
    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  };

  focusSidebarInitial = () => {
    if (!this.sidebarOpen || !this.isSidebarModal()) return;
    const { closeSidebar, sidebarContent } = this.domNodes;
    const focusableElements = this.getSidebarFocusableElements();
    const initialFocus = focusableElements.includes(closeSidebar)
      ? closeSidebar
      : focusableElements[0] || sidebarContent;
    this.focusSidebarElement(initialFocus);
  };

  restoreSidebarFocus = () => {
    const target = this.sidebarTrigger?.isConnected ? this.sidebarTrigger : this.domNodes?.openSidebar;
    this.focusSidebarElement(target);
    this.sidebarTrigger = null;
  };

  handleSidebarKeydown = (event) => {
    if (!this.sidebarOpen || !this.isSidebarModal()) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeSidebarFilter();
      return;
    }
    if (event.key !== "Tab") return;

    const focusableElements = this.getSidebarFocusableElements();
    if (focusableElements.length === 0) {
      event.preventDefault();
      this.focusSidebarElement(this.domNodes.sidebarContent);
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    if (
      event.shiftKey &&
      (document.activeElement === first ||
        !this.domNodes.sidebarContent.contains(document.activeElement))
    ) {
      event.preventDefault();
      this.focusSidebarElement(last);
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      this.focusSidebarElement(first);
    }
  };

  handleSidebarControlKeydown = (event) => {
    if (!["Enter", " ", "Spacebar"].includes(event.key)) return;
    event.preventDefault();
    if (event.repeat) return;
    event.currentTarget.click();
  };

  handleSidebarFocusIn = (event) => {
    if (
      this.sidebarOpen &&
      this.isSidebarModal() &&
      !this.domNodes.sidebarContent.contains(event.target)
    ) {
      this.focusSidebarInitial();
    }
  };

  startSidebarFocusManagement = () => {
    this.stopSidebarFocusManagement();
    document.addEventListener("keydown", this.handleSidebarKeydown);
    document.addEventListener("focusin", this.handleSidebarFocusIn);
  };

  stopSidebarFocusManagement = () => {
    document.removeEventListener("keydown", this.handleSidebarKeydown);
    document.removeEventListener("focusin", this.handleSidebarFocusIn);
  };

  lockSidebarScroll = () => {
    if (this.sidebarScrollLocked) return;
    document.documentElement.classList.add("prevent-scroll");
    this.sidebarScrollLocked = true;
  };

  unlockSidebarScroll = () => {
    if (!this.sidebarScrollLocked) return;
    document.documentElement.classList.remove("prevent-scroll");
    this.sidebarScrollLocked = false;
  };

  resetSidebarState = () => {
    if (this.sidebarCloseTimeout) {
      clearTimeout(this.sidebarCloseTimeout);
      this.sidebarCloseTimeout = null;
    }
    this.stopSidebarFocusManagement();
    this.unlockSidebarScroll();
    this.sidebarOpen = false;
    this.sidebarTrigger = null;
  };

  toggleView = (col) => {
    if (!col || this.view === "search") return;
    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;
    // Resize image product card
    const allImages = productContainer.querySelectorAll("responsive-image");
    if (allImages && allImages.length > 0) {
      allImages.forEach((img) => {
        img.disconnectedCallback();
        img.connectedCallback();
      });
    }

    const activeCol = this.activeCol || this.initialCol;
    if (this.showColSwitchers) {
      cols[activeCol - 1].classList.remove("active");
      cols[col - 1].classList.add("active");
    }

    productContainer.classList.remove(`m-cols-${activeCol}`);
    productContainer.classList.add(`m-cols-${col}`);

    window.localStorage.setItem(this.STORAGE_KEY, col);
    this.activeCol = col;
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return; // List layout
    const device = this.getDeviceByScreenSize();
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[device];
    if (maxCol < this.activeCol) this.toggleView(maxCol);
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreProducts();
          });
        },
        { threshold: 1 }
      );

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  loadMoreProducts = () => {
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;

    const { productContainer, loadMoreBtn } = this.domNodes;
    this.toggleLoading(true);
    fetchSection(this.sectionId, { fromCache: true, params: { page: nextPage } })
      .then((productGridHTML) => {
        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        productNodes.forEach((prodNode) => productContainer.appendChild(prodNode));
        this.initProducts();
      })
      .catch((err) => console.error(`Failed to load more products.`, err))
      .finally(() => {
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = nextPage;
        if (nextPage >= this.totalPages) {
          loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
        }
      });
  };

  initProducts = () => {
    MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
    MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      const method = loading ? "add" : "remove";
      loadMoreBtn.classList[method]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    const extraSpace = toolbar && toolbar.offsetTop;
    let lastScroll = 0;
    window.addEventListener("scroll", () => {
      if (MinimogTheme.config.mqlMobile) {
        const currentScroll = window.scrollY;
        if (currentScroll <= extraSpace) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }
        toolbar.classList.add("m-collection-toolbar--sticky");
        if (
          currentScroll > toolbar.offsetHeight + extraSpace &&
          currentScroll > lastScroll &&
          !toolbar.classList.contains("scroll-down")
        ) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }
        lastScroll = currentScroll;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    });
  }
}
